import { useTranslation } from "react-i18next";

import { Draggable } from "@/components/Draggable/Draggable";
import { Droppable } from "@/components/Droppable/Droppable";

import { CellBoundaries } from "@/hooks/useCellBoundaries/useCellBoundaries";

import { FormattedCellKey } from "@/pages/EditSubjectPlan/types/types";

import {
  TableData,
  TableResource,
} from "@/states/unitTableReducer/unitTableReducer";

import { NewUnitCard } from "../NewUnitCard/NewUnitCard";

import { CardActionMenu } from "./components/CardActionMenu/CardActionMenu";
import { DraggableHandle } from "./components/DraggableHandle/DraggableHandle";
import { TABLE_UNIT_CARD_NS } from "./TableUnitCard.i18n";

export const TableUnitCard = ({
  cellBoundaries,
  cellId,
  curriculumId,
  customUnit,
  meta,
  onRemoveUnit,
  onUpdateCustomUnit,
  onUpsertAdaptationNote,
  resource,
  schemeId,
  subjectId,
  tableData,
}: {
  cellId: string;
  cellBoundaries: CellBoundaries;
  meta:
    | {
        resourceId: string;
        row: string;
        column: string;
        cellIndex: number;
        cellSpan: number;
        cellId: string;
      }
    | {
        row: string;
        column: string;
        cellIndex: number;
        cellSpan: number;
        cellId: string;
      };
  onRemoveUnit: (payload: { cellId: string }) => void;
  onUpdateCustomUnit?: (data: {
    id: string;
    row: string;
    column: string;
    span?: number;
    title: string;
    description?: string;
  }) => void;
  onUpsertAdaptationNote: (payload: {
    cellId: string;
    adaptationNote: string;
  }) => void;
  customUnit?: {
    id: string;
    title: string;
    description?: string;
    cohort: string;
    startOnColumn: string;
    span: number;
  };
  resource: TableResource;
  curriculumId: string;
  schemeId?: string;
  subjectId: string;
  tableData: Pick<TableData, "cells" | "rows" | "columns" | "resources">;
}) => {
  const dndId = `${meta.cellId}-${meta.row}-${meta.column}`;
  const cellKey: FormattedCellKey = `${meta.row}__${meta.column}`;

  const { t } = useTranslation(TABLE_UNIT_CARD_NS);
  return (
    <div className="relative group">
      <DraggableHandle
        className="left-[4px]"
        direction="left"
        dndId={dndId}
        meta={{
          cellId: meta.cellId,
          row: meta.row,
          column: meta.column,
          cellIndex: meta.cellIndex,
          cellSpan: meta.cellSpan,
        }}
      />
      <Droppable
        data={{
          target: "table-cell",
          data: {
            ...meta,
            cellBoundaries: cellBoundaries[cellKey] ?? {},
          },
        }}
        id={`${dndId}-table-cell-droppable`}
        render={({ isOver }) => (
          <div
            aria-label={t("aria.table-unit-card", {
              unitTitle: resource.name,
            })}
            className={[
              "relative border rounded-[8px] hover:border-[#C5CAD3]",
              isOver ? "bg-blue-50 border-blue-500" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            role="group"
          >
            <Draggable
              className={[
                "card",
                "relative bg-white flex justify-between w-auto rounded-[8px] py-1 shadow-[0_1px_2px_0px_rgba(0,0,0,0.1)] gap-2 px-4 cursor-grab",
                // All styles on card related to dragging
              ].join(" ")}
              data={{
                target: "table-cell",
                data: meta,
              }}
              dragOverlayClassName="outline-2 outline-blue-500 rotate-[5.2deg] max-w-[170px]"
              id={`${dndId}-table-cell-draggable`}
              render={({ isDragging }) => (
                <NewUnitCard
                  className="border-neutral-150 hover:border-[#C5CAD3] group-active:border-blue-500 group-has-[&.handle-dragging]:border-blue-500! group-has-[&.handle-dragging]:border-1!"
                  isDragging={isDragging}
                  resource={resource}
                />
              )}
            />

            <div className="flex flex-col justify-center absolute right-[20px] top-1/2 transform -translate-y-1/2">
              <CardActionMenu
                cellId={cellId}
                curriculumId={curriculumId}
                customUnit={customUnit}
                schemeId={schemeId}
                subjectId={subjectId}
                tableData={tableData}
                onRemoveUnit={onRemoveUnit}
                onUpdateCustomUnit={onUpdateCustomUnit}
                onUpsertAdaptationNote={onUpsertAdaptationNote}
              />
            </div>
          </div>
        )}
      />

      <DraggableHandle
        className="right-[4px]"
        direction="right"
        dndId={dndId}
        meta={{
          cellId: meta.cellId,
          row: meta.row,
          column: meta.column,
          cellIndex: meta.cellIndex,
          cellSpan: meta.cellSpan,
        }}
      />
    </div>
  );
};
